/**
 * COMMERCE-WORKSPACE-000002 — Inventory founder workspace.
 * SoT: MarketSync domain=inventory. COMMERCE-MODEL-000001.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type InventoryPage as InventoryPageResult,
  type InventoryRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { FounderCommerceShell } from './workspace/FounderCommerceShell';
import {
  AsinLink,
  ComingSoonCell,
  SkuLink,
  WorkspaceLegend,
  type FieldSource,
} from './workspace/commerceFields';
import { useCommerceDomainHealth } from './workspace/useCommerceDomainHealth';

function src(r: InventoryRow, key: string, fallback: FieldSource = 'COMING_SOON'): FieldSource {
  const v = r.field_sources?.[key];
  if (
    v === 'SP-API' ||
    v === 'MarketSync' ||
    v === 'Derived' ||
    v === 'Calculated' ||
    v === 'Estimated' ||
    v === 'COMING_SOON'
  ) {
    return v;
  }
  return fallback;
}

function qtyCell(value: number | null | undefined, source: FieldSource) {
  if (value == null || source === 'COMING_SOON') return <ComingSoonCell />;
  return (
    <span className="font-mono tabular-nums" title={`Source: ${source}`}>
      {value}
    </span>
  );
}

export function MarketSyncInventoryPage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params] = useSearchParams();

  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(25);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [sortCol, setSortCol] = useState('sku');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [conditionFilter, setConditionFilter] = useState('');
  const [result, setResult] = useState<InventoryPageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  useEffect(() => {
    if (bootstrapped) return;
    const uq = params.get('q') || params.get('asin') || '';
    if (uq) {
      setQ(uq);
      setQDraft(uq);
    }
    setBootstrapped(true);
  }, [params, bootstrapped]);

  const connection = connections.find((c) => c.id === connectionId);
  const defaultMp =
    connection?.marketplace_ids?.[0] ?? connection?.marketplaces?.[0]?.marketplace_id;
  const flexCodes = connection?.metadata?.seller_flex_site_codes;
  const health = useCommerceDomainHealth(connectionId, 'inventory');
  const sortParam = order === 'desc' ? `${sortCol}_desc` : sortCol;

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.listInventoryPage(connectionId, {
        page,
        page_size: pageSize,
        q: q || undefined,
        sort: sortParam,
        order,
        status: conditionFilter || undefined,
      });
      setLatencyMs(Math.round(performance.now() - t0));
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load inventory');
      setResult(null);
      setLatencyMs(undefined);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, q, sortParam, order, conditionFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = result?.items ?? [];
  const meta: DataGridMeta | null = result
    ? {
        total_count: result.total_count,
        returned_count: result.returned_count ?? result.count,
        page: result.page,
        page_size: result.page_size,
        pages: result.pages,
        remaining: result.remaining,
        has_more: result.has_more,
        execution_time_ms: result.execution_time_ms ?? latencyMs,
        source: result.source,
        data_source_badge: result.source || 'MarketSync · inventory',
        last_sync: health.lastSync || connection?.last_sync_at || rows[0]?.last_sync,
        amazon_count: health.amazonCount,
      }
    : null;

  const columns: DataGridColumn<InventoryRow>[] = useMemo(
    () => [
      {
        id: 'sku',
        header: 'SKU',
        width: 120,
        sortable: true,
        stickyLeft: 0,
        render: (r) => <SkuLink sku={r.sku} connectionId={connectionId} />,
        exportValue: (r) => r.sku,
      },
      {
        id: 'asin',
        header: 'ASIN',
        width: 120,
        sortable: true,
        render: (r) =>
          !r.asin || src(r, 'asin') === 'COMING_SOON' ? (
            <ComingSoonCell />
          ) : (
            <AsinLink
              asin={r.asin}
              marketplaceId={r.marketplace_id || defaultMp}
              connectionId={connectionId}
            />
          ),
        exportValue: (r) => r.asin ?? '',
      },
      {
        id: 'fba_inventory',
        header: 'FBA',
        width: 88,
        sortable: true,
        render: (r) =>
          qtyCell(
            r.fba_inventory ?? (src(r, 'fba_inventory') === 'MarketSync' ? r.available : null),
            src(r, 'fba_inventory', 'MarketSync'),
          ),
        exportValue: (r) =>
          r.fba_inventory != null ? String(r.fba_inventory) : String(r.available ?? 'COMING SOON'),
      },
      {
        id: 'mfn_inventory',
        header: 'MFN',
        width: 88,
        render: (r) => qtyCell(r.mfn_inventory, src(r, 'mfn_inventory')),
        exportValue: (r) => (r.mfn_inventory != null ? String(r.mfn_inventory) : 'COMING SOON'),
      },
      {
        id: 'seller_flex_inventory',
        header: 'Flex',
        width: 88,
        render: (r) => qtyCell(r.seller_flex_inventory, src(r, 'seller_flex_inventory')),
        exportValue: (r) =>
          r.seller_flex_inventory != null ? String(r.seller_flex_inventory) : 'COMING SOON',
      },
      {
        id: 'transit_inventory',
        header: 'Transit',
        width: 88,
        render: (r) => qtyCell(r.transit_inventory, src(r, 'transit_inventory')),
        exportValue: (r) =>
          r.transit_inventory != null ? String(r.transit_inventory) : 'COMING SOON',
      },
      {
        id: 'total_inventory',
        header: 'Total',
        width: 88,
        render: (r) => qtyCell(r.total_inventory, src(r, 'total_inventory')),
        exportValue: (r) => (r.total_inventory != null ? String(r.total_inventory) : 'COMING SOON'),
      },
      {
        id: 'bsr',
        header: 'BSR',
        width: 80,
        sortable: true,
        render: (r) => qtyCell(r.bsr, src(r, 'bsr')),
        exportValue: (r) => (r.bsr != null ? String(r.bsr) : 'COMING SOON'),
      },
    ],
    [defaultMp, connectionId],
  );

  return (
    <FounderCommerceShell
      domain="inventory"
      title="Inventory"
      subtitle={`Channel quantities for SKU. Total only when all sources are real (never a fake sum).${
        flexCodes ? ` Flex codes: ${flexCodes}.` : ' Set Flex site codes on Connect when needed.'
      }`}
      entryKey="SKU"
      connection={connection}
      domainBadge={health.quality || 'inventory'}
      legend={<WorkspaceLegend />}
    >
      <PlatformDataGrid
        gridId="inventory-workspace-v2"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={rows}
        rowKey={(r) => `${r.sku}|${r.asin ?? ''}|${r.fnsku ?? ''}`}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage="No inventory rows. Run Inventory sync from Connect, then refresh."
        emptyAction={
          <Link to="/amazon/connect" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
            Open Connect
          </Link>
        }
        page={page}
        pageSize={pageSize}
        sort={sortCol}
        order={order}
        searchDraft={qDraft}
        searchApplied={q || undefined}
        onSearchDraftChange={setQDraft}
        onSearch={() => {
          setQ(qDraft.trim());
          setPage(1);
        }}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onSortChange={(col, ord) => {
          setSortCol(col);
          setOrder(ord);
          setPage(1);
        }}
        onRefresh={() => void load()}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        density="compact"
        toolbarExtra={
          <>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Connection</span>
              <select
                className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
                value={connectionId}
                onChange={(e) => {
                  setConnectionId(e.target.value);
                  setPage(1);
                  setSelected(new Set());
                }}
              >
                {connections.length === 0 && <option value="">No connections</option>}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Condition</span>
              <select
                className="mt-1 block min-h-11 min-w-[10rem] rounded-lg border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
                value={conditionFilter}
                onChange={(e) => {
                  setConditionFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="NewItem">NewItem</option>
                <option value="Used">Used</option>
                <option value="Refurbished">Refurbished</option>
              </select>
            </label>
          </>
        }
      />
    </FounderCommerceShell>
  );
}
